import { useState } from "react";
import { OutcomeHist } from "./OutcomeHist";
import { initials, shortDate, signed, bookPct, MIN_BOOK_PCT } from "./srUtil";

const OutcomeCell = ({ b, sector, onOpen }) =>
  b.outcomes ? (
    <button className="outcomebutton" onClick={onOpen} aria-label={`See ${b.fund} track record`}
      data-testid={`sr-fund-outcome-${b.cik}`}>
      <div className="outcomestats">
        <b className="green">{b.outcomes.p_gt10}% <small>beat sector by &gt;10%</small></b>
        <b>{b.outcomes.hit_pct}% <small>beat sector</small></b>
      </div>
      <OutcomeHist pct={b.outcomes.hist_pct} title={`${b.outcomes.n} twelve-month ${sector}-relative outcomes`} />
      <small>Based on {b.outcomes.n} past picks, each held 12 months</small>
    </button>
  ) : (
    <small className="muted" data-testid={`sr-fund-outcome-${b.cik}`}>Fewer than 5 past {sector} picks to judge</small>
  );

export const fundDetail = (b, sector, skillAsOf, quarterEnd) => {
  const o = b.outcomes;
  return {
    title: b.fund,
    body: (
      <>
        <p>
          {b.top_pct != null && <span className="pill good">Top {b.top_pct}% in {sector}</span>}
          {b.specialist && <span className="pill">Sector specialist</span>}
        </p>
        {o ? (
          <>
            <OutcomeHist pct={o.hist_pct} title={`${o.n} past picks`} testid="sr-fund-detail-hist" />
            <div className="outcomestats"><b>{o.p_gt10}% beat the sector by more than 10%</b><b>{signed(o.median)}% typical result</b></div>
            <p>We looked at this fund’s {o.n} past {sector} picks and how each did over the following 12 months against the sector.</p>
            <p><b>Average winner:</b> {signed(o.avg_win)}%<br />
              <b>Average loser:</b> {signed(o.avg_loss)}%<br />
              <b>Half of its picks landed between</b> {signed(o.p25)}% and {signed(o.p75)}%.</p>
            <p>A good record raises the odds; it doesn’t guarantee this pick works.</p>
          </>
        ) : <p>This fund has fewer than five past {sector} picks, so there isn’t enough history to show a track record.</p>}
        <p><b>Ranking:</b> {b.top_pct != null ? `its ${sector} record is in the top ${b.top_pct}% of funds` : `not ranked in ${sector}`} (as of{" "}
          {shortDate(skillAsOf, true)}).</p>
        <p><b>This position:</b> {bookPct(b.pct_of_book)} of its reported portfolio (${(b.value_usd / 1e6).toFixed(2)}m) on{" "}
          {shortDate(quarterEnd, true)}; the filing became public on {shortDate(b.revealed, true)}.</p>
      </>
    ),
  };
};

const bigEnough = (b) => (b.pct_of_book ?? 0) >= MIN_BOOK_PCT;

const Conviction = ({ b, quarterEnd }) => (
  <td data-testid={`sr-fund-conviction-${b.cik}`}><span className="mobilelabel">Position size</span>
    {bigEnough(b) ? `${bookPct(b.pct_of_book)} of portfolio` : <span className="muted">Under {MIN_BOOK_PCT}% of portfolio</span>}
    <small>As at {shortDate(quarterEnd, true)}</small></td>
);

const FundRow = ({ b, sector, quarterEnd, onOpen }) => (
  <tr data-testid={`sr-fund-row-${b.cik}`}>
    <td>
      <button className="fundname" onClick={onOpen} data-testid={`sr-fund-name-${b.cik}`}>
        <span className="avatar">{initials(b.fund)}</span>{b.fund}
      </button>
      <small>{b.specialist ? "★ Sector specialist" : "High-conviction buyer"}</small>
      <small>{b.top_pct != null ? `Top ${b.top_pct}% in ${sector}` : `Not ranked in ${sector}`}</small>
    </td>
    <td className="outcomecell"><OutcomeCell b={b} sector={sector} onOpen={onOpen} /></td>
    <Conviction b={b} quarterEnd={quarterEnd} />
  </tr>
);

// Only sector specialists (any size) and positions of at least MIN_BOOK_PCT of the fund's book are shown.
export const relevantBuyers = (buyers) => [
  ...buyers.filter((b) => b.specialist),
  ...buyers.filter((b) => !b.specialist && bigEnough(b)).sort((a, b) => b.pct_of_book - a.pct_of_book),
];

const INITIAL = 5;

export const BuyersTable = ({ cur, sector, quarter, symbol, onDetail }) => {
  const [all, setAll] = useState(false);
  const { buyers, chart } = cur;
  const rows = relevantBuyers(buyers), hidden = buyers.length - rows.length;
  const shown = all ? rows : rows.slice(0, INITIAL);
  if (!rows.length) {
    return (
      <div className="card pad" data-testid="sr-buyers-empty">
        <h3>No high-conviction buyers</h3>
        <p className="muted">None of the {buyers.length} fund{buyers.length !== 1 ? "s" : ""} that bought {symbol} put {MIN_BOOK_PCT}% or
          more of its portfolio into it, and none is a {sector} specialist.</p>
      </div>
    );
  }
  return (
    <div className="card">
      <table data-testid="sr-buyers-table">
        <thead><tr><th>Investor</th><th>Track record in {sector}</th><th>Size of position</th></tr></thead>
        <tbody>
          {shown.map((b) => (
            <FundRow key={b.cik} b={b} sector={sector} quarterEnd={chart.q_end}
              onOpen={() => onDetail(fundDetail(b, sector, cur.skill_as_of, chart.q_end))} />
          ))}
        </tbody>
      </table>
      <div className="footnote row between">
        <span data-testid="sr-buyers-footnote">{hidden > 0 && `${hidden} other fund${hidden !== 1 ? "s" : ""} bought smaller positions (under ${MIN_BOOK_PCT}% of their portfolio). `}
          {quarter} buying, holdings as of {shortDate(chart.q_end, true)}. Filings don’t show who still owns it today.</span>
        {rows.length > INITIAL && (
          <button className="textlink" aria-expanded={all} style={{ whiteSpace: "nowrap" }} onClick={() => setAll(!all)}
            data-testid="sr-all-buyers-toggle">{all ? `Show top ${INITIAL} ↑` : `All ${rows.length} relevant buyers ↓`}</button>
        )}
      </div>
    </div>
  );
};
